import React, { useState, useEffect, useCallback } from 'react';
import LayoutWrapper from '../../../../shared/components/LayoutWrapper';
import SmartTable, { SmartTableColumn } from '../../../../shared/components/SmartTable';
import SmartTableButtons from '../../../../shared/components/SmartTableButtons';
import SearchableDropdown from '../../../../shared/components/SearchableDropdown';
import { useAuth } from '../../../../shared/hooks/useAuth';
import api from '../../../../shared/utils/api';
import SuccessPopup from '../../../../shared/components/SuccessPopup';
import ErrorPopup from '../../../../shared/components/ErrorPopup';

interface Receipt {
  Oid: string;
  CustomerOid?: string;
  Date?: string;
  Number?: string;
  BillId?: string;
  YearType?: number;
  PeriodType?: number;
  DayType?: number;
  BusinessCustomerOid?: string;
  BusinessCustomerRd?: string;
  BusinessCustomerName?: string;
  BusinessClassName?: string;
  CityTaxSalesAmount?: number;
  VATPayableAmount?: number;
  NonVATSalesAmount?: number;
  VATInclusiveSalesAmount?: number;
  CityTaxPurchaseAmount?: number;
  VATReceivableAmount?: number;
  NonVATPurchaseAmount?: number;
  VATInclusivePurchaseAmount?: number;
  ReceiptType?: string;
  [key: string]: any;
}

interface Customer {
  Oid: string;
  CustomerID: string;
  CustomerName: string;
  [key: string]: any;
}

const formatNumber = (value: any) => {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (value: any) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
};

const NoLedgerTransactionListPage: React.FC = () => {
  const { userAuth } = useAuth();
  
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Filter state
  const [customerOid, setCustomerOid] = useState<string>('CB734CB0-90B9-4791-BC6D-CB842C1BCD84');
  const [yeartype, setYeartype] = useState<string>('2025');
  
  // Customer list state
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  const defaultColumns: SmartTableColumn[] = [
    { key: 'Oid', label: 'Oid', width: 200 },
    { key: 'CustomerOid', label: 'Customer Oid', width: 200 },
    { key: 'Date', label: 'Огноо', width: 120, render: (value) => formatDate(value) },
    { key: 'Number', label: 'Дугаар', width: 120 },
    { key: 'BillId', label: 'Bill ID', width: 150 },
    { key: 'YearType', label: 'Жил', width: 80 },
    { key: 'PeriodType', label: 'Сар', width: 80 },
    { key: 'DayType', label: 'Өдөр', width: 80 },
    { key: 'BusinessCustomerOid', label: 'Бизнес харилцагч Oid', width: 200 },
    { key: 'BusinessCustomerRd', label: 'Бизнес харилцагч RD', width: 150 },
    { key: 'BusinessCustomerName', label: 'Бизнес харилцагч нэр', width: 200 },
    { key: 'BusinessClassName', label: 'Бизнес ангилал', width: 150 },
    { key: 'CityTaxSalesAmount', label: 'Хотын татвар (борлуулалт)', width: 150, render: (value) => formatNumber(value) },
    { key: 'VATPayableAmount', label: 'НӨАТ төлөх дүн', width: 120, render: (value) => formatNumber(value) },
    { key: 'NonVATSalesAmount', label: 'НӨАТ-гүй борлуулалт', width: 150, render: (value) => formatNumber(value) },
    { key: 'VATInclusiveSalesAmount', label: 'НӨАТ-тай борлуулалт', width: 150, render: (value) => formatNumber(value) },
    { key: 'CityTaxPurchaseAmount', label: 'Хотын татвар (худалдан авалт)', width: 180, render: (value) => formatNumber(value) },
    { key: 'VATReceivableAmount', label: 'НӨАТ авна', width: 120, render: (value) => formatNumber(value) },
    { key: 'NonVATPurchaseAmount', label: 'НӨАТ-гүй худалдан авалт', width: 180, render: (value) => formatNumber(value) },
    { key: 'VATInclusivePurchaseAmount', label: 'НӨАТ-тай худалдан авалт', width: 180, render: (value) => formatNumber(value) },
    { key: 'ReceiptType', label: 'Баримтын төрөл', width: 120 },
  ];

  // Fetch customers list
  const fetchCustomers = useCallback(async () => {
    setLoadingCustomers(true);
    try {
      const result = await api.get('/api/customerslist/accountant/customers');
      
      if (result?.success && result?.data) {
        setCustomers(result.data);
      }
    } catch (err: any) {
      console.error('❌ Customers fetch error:', err);
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  // Fetch receipts
  const fetchReceipts = useCallback(async () => {
    if (!customerOid || !yeartype) {
      setErrorMessage('CustomerOid болон yeartype оруулна уу');
      setShowErrorPopup(true);
      return;
    }

    setLoading(true);
    setErrorMessage('');
    
    try {
      const result = await api.get(
        `/api/no-ledger-transaction-list?CustomerOid=${encodeURIComponent(customerOid)}&yeartype=${encodeURIComponent(yeartype)}`
      );
      
      if (result?.success) {
        setReceipts(result.data || []);
        setSuccessMessage(`${result.count || 0} мөр олдлоо`);
        setShowSuccessPopup(true);
      } else {
        throw new Error(result?.message || 'Мэдээлэл авахад алдаа гарлаа');
      }
    } catch (err: any) {
      console.error('❌ No Ledger Transaction List fetch error:', err);
      setErrorMessage(err?.message || 'Мэдээлэл авахад алдаа гарлаа');
      setShowErrorPopup(true);
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [customerOid, yeartype]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleToggleSearch = () => {
    setShowColumnSearch(!showColumnSearch);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(!showColumnSettings);
  };

  const handleRefresh = () => {
    fetchReceipts();
  };

  // Customer dropdown options
  const customerOptions = customers.map(customer => ({
    value: customer.Oid,
    label: `${customer.CustomerName || ''} (${customer.CustomerID || ''})`
  }));

  // Selected customer name for display
  const selectedCustomerName = customers.find(c => c.Oid === customerOid) 
    ? `${customers.find(c => c.Oid === customerOid)?.CustomerName || ''} (${customers.find(c => c.Oid === customerOid)?.CustomerID || ''})`
    : '';

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Журналд холбогдоогүй баримт</h1>
          
          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* CustomerOid Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Харилцагч сонгох
                </label>
                <SearchableDropdown
                  options={customerOptions}
                  value={customerOid}
                  onChange={(value) => setCustomerOid(value)}
                  valueLabel={selectedCustomerName}
                  placeholder="Харилцагч сонгох"
                  className="w-full"
                  enableAutoComplete={true}
                />
              </div>

              {/* Yeartype Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Жил (yeartype)
                </label>
                <input
                  type="text"
                  value={yeartype}
                  onChange={(e) => setYeartype(e.target.value)}
                  placeholder="Жил оруулах (жишээ: 2025)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <button
                  onClick={fetchReceipts}
                  disabled={loading || !customerOid || !yeartype}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Уншиж байна...' : '🔍 Хайх'}
                </button>
              </div>
            </div>
          </div>

          {/* SmartTableButtons */}
          <div className="mb-4">
            <SmartTableButtons
              showColumnSearch={showColumnSearch}
              onToggleSearch={handleToggleSearch}
              onColumnSettings={handleColumnSettings}
              onRefresh={handleRefresh}
              addable={false}
            />
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center h-64">
              <div className="text-lg">Уншиж байна...</div>
            </div>
          )}

          {/* SmartTable */}
          {!loading && (
            <SmartTable
              data={receipts}
              columns={defaultColumns}
              fetchData={fetchReceipts}
              editable={false}
              addable={false}
              rowKey="Oid"
              settingsKey="admin-no-ledger-transaction-list"
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
            />
          )}
        </div>

        {/* Success Popup */}
        <SuccessPopup
          message={successMessage}
          isVisible={showSuccessPopup}
          onClose={() => setShowSuccessPopup(false)}
          duration={3000}
        />

        {/* Error Popup */}
        <ErrorPopup
          message={errorMessage}
          isVisible={showErrorPopup}
          onClose={() => setShowErrorPopup(false)}
          duration={5000}
        />
      </div>
    </LayoutWrapper>
  );
};

export default NoLedgerTransactionListPage;
